import { CheckCircle2, AlertCircle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { BannerState } from '../types';

interface AddToCartBannerProps {
  banner: BannerState;
  onDismiss: () => void;
  onRetry: (retryPayload: NonNullable<BannerState['retryPayload']>) => void;
}

export function AddToCartBanner({
  banner,
  onDismiss,
  onRetry,
}: AddToCartBannerProps) {
  const isSuccess = banner.kind === 'success';
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'flex items-center gap-3 rounded-md border px-3 py-2',
        isSuccess
          ? 'border-success/30 bg-success-soft text-success-soft-foreground'
          : 'border-destructive/30 bg-destructive-soft text-destructive-soft-foreground'
      )}
    >
      <div className="shrink-0">
        {isSuccess ? (
          <CheckCircle2 className="h-5 w-5 text-success" />
        ) : (
          <AlertCircle className="h-5 w-5 text-destructive" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold">{banner.title}</p>
        <p className="truncate text-xs text-muted-foreground">{banner.detail}</p>
      </div>
      {!isSuccess && banner.retryPayload && (
        <Button
          variant="primaryOutline"
          size="sm"
          onClick={() => banner.retryPayload && onRetry(banner.retryPayload)}
        >
          Try Again
        </Button>
      )}
      <button
        type="button"
        aria-label="Dismiss"
        onClick={onDismiss}
        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/25 max-lg:h-11 max-lg:w-11"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
